import React, { FC } from 'react';

import { styled } from '@mui/material';
import { Link } from 'react-router-dom';

import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { BodyText, H3 } from '~/components/UI/Texts';
import { useTenantContext } from '~/context/TenantProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { PricingPlansType } from '~/pages/PricePage/types';
import { PRICING_ROUTE, PricingRoutes } from '~/routes';
import {
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { Maybe } from '~/types';
import assertNever from '~/utils/errors/assertNever';
import { TENANTS_CONFIG, TenantsRefs } from '~/utils/tenantsConfig';

type CurrentPlanPanelProps = {
  selectedPricingPlans: PricingPlansType;
};

type TenantName = Maybe<TenantsRefs> | undefined;

const getIndividualDescriptionKey = (tenantName: TenantName) => {
  switch (tenantName) {
    case TenantsRefs.Dsu:
      return 'page.price.forBusinessDescription.data';
    case TenantsRefs.Su:
    case TenantsRefs.Bpp:
    case TenantsRefs.Dukece:
      return 'page.price.forBusinessDescription.sustainabilityunlocked';
    case TenantsRefs.Fu:
    case TenantsRefs.Em:
      return 'page.price.forBusinessDescription.financeunlocked';
    case TenantsRefs.Xunl:
    case TenantsRefs.Ssku:
    case null:
    case undefined:
      return 'page.price.forBusinessDescription.default';
    default:
      assertNever(tenantName);
  }
};

const CurrentPlanPanel: FC<CurrentPlanPanelProps> = ({ selectedPricingPlans }) => {
  const { t } = useTenantTranslation();
  const { tenant } = useTenantContext();

  const isBusiness = selectedPricingPlans === 'business';

  const heading = isBusiness ? t('page.price.forIndividual') : t('page.price.forBusiness');

  const forIndividualTextKey =
    tenant?.name && TENANTS_CONFIG[tenant.name]
      ? TENANTS_CONFIG[tenant.name].pricingPage?.forIndividualDescriptionTextKey
      : 'page.price.forIndividualDescription';

  const description = isBusiness
    ? t(forIndividualTextKey)
    : t(getIndividualDescriptionKey(tenant?.name as TenantName));

  return (
    <StyledPanel>
      <HeadingContainer>
        <H3>{heading}</H3>
        <BodyText>{description}</BodyText>
      </HeadingContainer>
      <Link
        to={`${PRICING_ROUTE}${isBusiness ? PricingRoutes.Individual : PricingRoutes.Business}`}
      >
        <CustomButton color="secondary" variant="outlined">
          {t('page.price.seePricing')}
        </CustomButton>
      </Link>
    </StyledPanel>
  );
};

export default CurrentPlanPanel;

const StyledPanel = styled('div')`
  background: ${({ theme }) => theme.palette.common.blackSecondary};
  border-radius: ${MIDDLE_RADIUS_PX};
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${SUBMIDDLE_MARGIN_PX};
  padding: ${SUBMIDDLE_MARGIN_PX} ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    justify-content: space-between;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding: ${MIDDLE_MARGIN_PX};
  }
`;
const HeadingContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${TINY_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    gap: 0;
  }
`;
